import { mapActions, mapState } from 'vuex'
import csvExport from '@/services/CSVExport'
import _ from 'lodash'
import moment from 'moment'

/**
 * Mixins for RankZone report tabs
 */
export const baseComponent = {
  created () {
    this.$emit('export-disable', true)
  },
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false
    }
  },
  watch: {
    isDataUpdating () {
      this.$emit('export-disable', this.isDataUpdating)
    },
    isDataFetched () {
      this.$emit('export-disable', !this.isDataFetched)
    }
  },
  computed: {
    ...mapState('rankZoneCommon', [
      'snapshots',
      'isSnapshotsLoaded'
    ]),
    snapshotsMissing () {
      return this.isSnapshotsLoaded && !this.snapshots.length
    },
    snapshotsPresent () {
      return this.isSnapshotsLoaded && Boolean(this.snapshots.length)
    }
  },
  methods: {
    ...mapActions('rankZoneCommon', ['addExpandedItem']),
    exportCsv () {
      if (this.isDataUpdating || this.snapshotsMissing) {
        return
      }
      let topTableData
      if (this.$route.name.toUpperCase() === 'URL') {
        topTableData = _.cloneDeep(this.urls)
      } else {
        topTableData = _.cloneDeep(this.tableData)
      }
      if (!topTableData || topTableData.length === 0) {
        return
      }
      const bottomTableData = _.cloneDeep(this.comparisonTableData)
      const headerLabels = this.config[ 0 ].cells.map(cell => cell.label.replace(/-/g, 'to'))
      const propNames = this.config[ 0 ].cells.map(cell => cell.propName)
      const data = [ headerLabels ]
      if (this.filters) {
        data.push([ this.filters.snapshot_date ])
      } else if (this.snapshot_date) {
        data.push([ this.snapshot_date ])
      }

      function fullFillData (source) {
        source.forEach(item => {
          const result = []
          propNames.forEach(name => {
            result.push(item[ name ])
          })
          data.push(result)
          if (item.children && item.children.length > 0) {
            const urls = []
            item.children.forEach(child => {
              if (child.children && child.children.length > 0 && child.children[ 0 ].type === 'url') {
                urls.push(...child.children)
              }
            })
            urls.forEach(url => {
              const urlResult = []
              propNames.forEach(name => {
                urlResult.push(url[ name ])
              })
              data.push(urlResult)
            })
          }
        })
      }

      fullFillData(topTableData)
      if (bottomTableData && bottomTableData.length > 0) {
        data.push([ this.comparingData.snapshot_date ])
        fullFillData(bottomTableData)
      }
      csvExport(`${this.selectedClient.name.replace(/ /g, '_')}_RankZone_${this.$route.name}_${moment().format('YYYY-MM-DD')}`, data)
    }
  }
}

/**
     *  table config
     *  @example:
     *  [{type: 'top-level',    //level
     *  clickable: true,        //allow click on row title
        children: 'children',   //children property name
        isChildrenLoadingLevel: true, // is used to notify child component BaseTableCollapsibleRow about necessity to load child data from backend
        highlightProp: 'type', // is used when we need to change color of concrete cell with propName == highlightProp and cell content == highlightValue
        highlightValue: 'Siblings',
        colorMap: [],           // color indicators list for first column. is used on Competition.vue
        colorProperty: 'date',   // name of trigger property (propName) to change indicator color
        cells: [                // array of table cells with column settings
          {label: 'URL Status', // label of first column
            subtitleSingle: 'status found',   // subtitle name settings
            subtitlePlural: 'statuses found', // subtitle name settings
            propName: 'label',   // name of property in received form backend data
            style: {              // style stuff
              'flex-basis': '33%'
            }
          },
          {label: '01 - 03',
            propName: '01 - 03',
            filterName: 'commaDividedNumber'  // name filter function that should be applied to value in the table cell
            },...]},
        {type: 'second-level',
        children: 'children',
        childrenLimit: 20,    //max length of children inner table if we are using inner pagination for children
        cells: [
          {propName: 'label',
            style: {
              'flex-basis': '33%'}
          },
          {propName: '01 - 03',
            filterName: 'commaDividedNumber'
          },...]
          }]
     **/
export const topLevelTableConfig = {
  data () {
    return {
      topLevelConfig: {
        type: 'top-level',
        children: 'children',
        cells: [
          {
            label: 'URL Status',
            subtitleSingle: 'status found',
            subtitlePlural: 'statuses found',
            propName: 'label',
            style: {
              'flex-basis': '33%'
            }
          },
          {
            label: '01 - 03',
            propName: '01 - 03',
            filterName: 'commaDividedNumber'
          },
          {
            label: '04 - 07',
            propName: '04 - 07',
            filterName: 'commaDividedNumber'
          },
          {
            label: '08 - 10',
            propName: '08 - 10',
            filterName: 'commaDividedNumber'
          },
          {
            label: '11 - 25',
            propName: '11 - 25',
            filterName: 'commaDividedNumber'
          },
          {
            label: '26 - 50',
            propName: '26 - 50',
            filterName: 'commaDividedNumber'
          },
          {
            label: '51 - 100',
            propName: '51 - 100',
            filterName: 'commaDividedNumber'
          },
          {
            label: 'NR',
            propName: 'NR',
            filterName: 'commaDividedNumber'
          },
          {
            label: 'ND',
            propName: 'ND',
            filterName: 'commaDividedNumber'
          },
          {
            label: 'Total',
            propName: 'total_msv',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          },
          {
            label: 'KW#',
            propName: 'keyword_total',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          }
        ]
      }
    }
  }
}

export const secondLevelTableConfig = {
  data () {
    return {
      secondLevelConfig: {
        type: 'second-level',
        children: 'children',
        childrenLimit: 20,
        cells: [
          {
            propName: 'label',
            style: {
              'flex-basis': '33%'
            }
          },
          {
            propName: '01 - 03',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '04 - 07',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '08 - 10',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '11 - 25',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '26 - 50',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '51 - 100',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'NR',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'ND',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'total_msv',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          },
          {
            propName: 'keyword_total',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          }
        ]
      }
    }
  }
}

export const thirdLevelTableConfig = {
  data () {
    return {
      thirdLevelConfig: {
        type: 'third-level',
        children: 'children',
        childrenLimit: 20,
        isChildrenLoadingLevel: true,
        linkConfig: {
          linkProp: 'label',
          hrefProp: 'label',
          contentBriefUrl: 'content_brief_url'
        },
        handleCalendarIconClick: this.openPopup,
        cells: [
          {
            propName: 'label',
            style: {
              'flex-basis': '33%'
            }
          },
          {
            propName: '01 - 03',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '04 - 07',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '08 - 10',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '11 - 25',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '26 - 50',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '51 - 100',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'NR',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'ND',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'total_msv',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          },
          {
            propName: 'keyword_total',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          }
        ]
      }
    }
  }
}

export const fourthLevelTableConfig = {
  data () {
    return {
      forthLevelConfig: {
        clickable: true,
        showHistory: true,
        type: 'fourth-level',
        childrenLimit: 20,
        cells: [
          {
            propName: 'label',
            style: {
              'flex-basis': '33%'
            },
            searchInGoogle: true
          },
          {
            propName: '01 - 03',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '04 - 07',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '08 - 10',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '11 - 25',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '26 - 50',
            filterName: 'commaDividedNumber'
          },
          {
            propName: '51 - 100',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'NR',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'ND',
            filterName: 'commaDividedNumber'
          },
          {
            propName: 'total_msv',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          },
          {
            propName: 'keyword_total',
            filterName: 'commaDividedNumber',
            style: {
              'flex-basis': '2%'
            }
          }
        ]
      }
    }
  }
}
